/**
 * One call's detail (brief §5 Phase 2): legs, the AA→queue→agent stage
 * timeline with per-stage durations, and inline recording playback
 * (Range-backed `<audio>`; gated by `recordings.play`).
 */

import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  getCdrDetail,
  recordingDownloadUrl,
  recordingPlayUrl,
} from "@pbxmgr/api";
import type { CdrRecording } from "@pbxmgr/api";
import { Alert, Badge, Card, Spinner } from "@pbxmgr/ui";

import { usePermission } from "../auth/usePermission";

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function ms(value: number | null): string {
  return value === null || value === undefined ? "—" : `${(value / 1000).toFixed(1)}s`;
}

function party(name: string | null, uriOrExt: string | null): string {
  if (name && uriOrExt) return `${name} <${uriOrExt}>`;
  return name ?? uriOrExt ?? "—";
}

function RecordingItem({ rec }: { rec: CdrRecording }): ReactNode {
  const canPlay = usePermission("recordings.play");
  const canDownload = usePermission("recordings.download");
  return (
    <div className="cdr-recording">
      <div className="cdr-recording__meta">
        <strong>
          {party(rec.from_party, null)} → {party(rec.to_party, null)}
        </strong>{" "}
        {rec.extension ? `ext ${rec.extension}` : ""} {formatWhen(rec.recorded_at)}
        {rec.duration_seconds !== null ? ` · ${rec.duration_seconds}s` : ""}
      </div>
      {rec.available ? (
        <>
          {canPlay ? (
            <audio controls preload="none" src={recordingPlayUrl(rec.id)} />
          ) : (
            <span className="ui-empty">No playback permission.</span>
          )}
          {canDownload ? <a href={recordingDownloadUrl(rec.id)}>Download</a> : null}
        </>
      ) : rec.fetch_error ? (
        <Alert>Fetch failed: {rec.fetch_error}</Alert>
      ) : (
        <Badge tone="degraded">processing</Badge>
      )}
    </div>
  );
}

export function CdrDetailPage(): ReactNode {
  const { cdrId } = useParams();
  const id = Number(cdrId);
  const detail = useQuery({
    queryKey: ["cdr-detail", id],
    queryFn: () => getCdrDetail(id),
    enabled: Number.isFinite(id),
  });

  if (detail.isLoading) return <Spinner />;
  if (detail.isError || !detail.data) return <Alert>Failed to load the call.</Alert>;
  const cdr = detail.data;

  return (
    <div>
      <div className="dash__head">
        <h1>Call {cdr.vodia_call_id ?? cdr.id}</h1>
        <Link to="/cdr">← back to report</Link>
      </div>

      <Card>
        <p>
          <strong>{cdr.from_uri ?? "—"}</strong> → <strong>{cdr.to_uri ?? "—"}</strong>
          {" · "}
          {cdr.direction ?? "—"} · {formatWhen(cdr.start_time)} ·{" "}
          {cdr.duration_seconds !== null ? `${cdr.duration_seconds}s` : "—"} ·{" "}
          {cdr.disposition ?? "—"} · node {cdr.source_node_id}
        </p>
      </Card>

      <section className="dash-panel">
        <h2>Recordings ({cdr.recordings.length})</h2>
        <Card>
          {cdr.recordings.length === 0 ? (
            <div className="ui-empty">No recordings for this call.</div>
          ) : (
            cdr.recordings.map((rec) => <RecordingItem key={rec.id} rec={rec} />)
          )}
        </Card>
      </section>

      <section className="dash-panel">
        <h2>Stage timeline ({cdr.states.length})</h2>
        <Card>
          {cdr.states.length === 0 ? (
            <div className="ui-empty">No stage data.</div>
          ) : (
            <table className="ui-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Stage</th>
                  <th>Account</th>
                  <th>Answered by</th>
                  <th>Ring</th>
                  <th>Talk</th>
                  <th>Hold</th>
                  <th>IVR</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {cdr.states.map((s) => (
                  <tr key={s.seq}>
                    <td>{s.seq}</td>
                    <td>{s.type ?? "—"}</td>
                    <td>{party(s.account_name, s.account)}</td>
                    <td>{party(s.answered_extension_name, s.answered_extension)}</td>
                    <td>{ms(s.duration_ring_ms)}</td>
                    <td>{ms(s.duration_talk_ms)}</td>
                    <td>{ms(s.duration_hold_ms)}</td>
                    <td>{ms(s.duration_ivr_ms)}</td>
                    <td>{s.reason ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </section>

      <section className="dash-panel">
        <h2>Legs ({cdr.legs.length})</h2>
        <Card>
          {cdr.legs.length === 0 ? (
            <div className="ui-empty">No leg data.</div>
          ) : (
            <table className="ui-table">
              <thead>
                <tr>
                  <th>Kind</th>
                  <th>Direction</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Extension</th>
                  <th>Trunk</th>
                  <th>Codec</th>
                  <th>MOS</th>
                  <th>Connected</th>
                  <th>Ended</th>
                </tr>
              </thead>
              <tbody>
                {cdr.legs.map((leg, i) => (
                  <tr key={i}>
                    <td>{leg.kind ?? "—"}</td>
                    <td>{leg.direction ?? "—"}</td>
                    <td>{leg.from_uri ?? "—"}</td>
                    <td>{leg.to_uri ?? "—"}</td>
                    <td>{party(leg.extension_name, leg.extension)}</td>
                    <td>{leg.trunk_name ?? "—"}</td>
                    <td>{leg.codec ?? "—"}</td>
                    <td>{leg.mos ?? "—"}</td>
                    <td>{formatWhen(leg.connect_time)}</td>
                    <td>{formatWhen(leg.end_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </section>
    </div>
  );
}
